import React from "react";
import {Controller, useForm, useWatch} from "react-hook-form";
import {FormattedMessage, useIntl} from "react-intl";
import {twJoin} from "tailwind-merge";
import {Button, defaultBorderColor, Select, SelectOptionProps, TextField} from "peggirkit";
import TextAddOn from "peggirkit/dist/components/Input/Field/TextField/TextFieldAddOn/TextAddOn";
import {getInputCurrencyProps, getInputSignedCurrencyProps, getInputValidationProps} from "@/data/Validation";
import {
    ProductOptionEditValues,
    productOptionEditValuesFormValidation,
    toAmount,
    VAT_RATES,
} from "@/data/entities/menuEditor";
import {label} from "@/utils/LabelUtils";
import MultiSelectCombobox, {MultiSelectOption} from "@widgets/input/combobox/MultiSelectCombobox";

type Fulfillment = "delivery" | "takeaway";

const vatOptions: SelectOptionProps<number>[] = VAT_RATES.map(rate => ({value: rate, displayValue: `${rate}%`}));

const defaultValues: ProductOptionEditValues = {
    name: "",
    price: "",
    priceTakeaway: "",
    deposit: "0,00",
    supFee: "0,00",
    vat: 9,
    soldout: false,
    delivery: true,
    takeaway: true,
};

type Props = {
    onAdd: (values: ProductOptionEditValues) => void,
    onCancel: () => void,
};

/**
 * Fields of a new option. It's added to the list; it's created when the
 * option group is saved.
 */
const NewOptionForm = ({onAdd, onCancel}: Props) => {
    const intl = useIntl();
    const {control, register, setValue, handleSubmit, formState: {errors}} = useForm<ProductOptionEditValues>({
        defaultValues,
    });
    const [delivery, takeaway] = useWatch({control, name: ["delivery", "takeaway"]});
    const fulfillmentOptions: MultiSelectOption<Fulfillment>[] = [
        {value: "delivery", displayValue: intl.formatMessage({id: "delivery"})},
        {value: "takeaway", displayValue: intl.formatMessage({id: "takeaway"})},
    ];
    const submit = handleSubmit((values: ProductOptionEditValues) => onAdd({
        ...values,
        name: values.name.trim(),
        price: toAmount(values.price),
        priceTakeaway: toAmount(values.priceTakeaway),
        deposit: toAmount(values.deposit),
        supFee: toAmount(values.supFee),
    }));

    return (
        <form
            className={twJoin(
                "w-full grid grid-cols-2 gap-5 p-4 rounded-lg border bg-gray-50 dark:bg-slate-900/40",
                defaultBorderColor,
            )}
            onSubmit={submit}
        >
            <h3 className={"col-span-2 font-medium text-gray-900 dark:text-white"}>
                <FormattedMessage id={"newOption"}/>
            </h3>

            <TextField
                containerClassname={"col-span-2"}
                type={"text"}
                label={label("name", true)}
                autoFocus
                error={errors.name && errors.name.message}
                {...register("name", {
                    ...getInputValidationProps(productOptionEditValuesFormValidation.name, intl),
                })}
            />

            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={label("priceDelivery", true)}
                leading={TextAddOn({text: "€"})}
                placeholder={"0,50"}
                error={errors.price && errors.price.message}
                {...register("price", {
                    ...getInputValidationProps(productOptionEditValuesFormValidation.price, intl),
                    ...getInputSignedCurrencyProps(intl),
                })}
            />

            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={label("priceTakeaway", true)}
                leading={TextAddOn({text: "€"})}
                placeholder={"0,50"}
                error={errors.priceTakeaway && errors.priceTakeaway.message}
                {...register("priceTakeaway", {
                    ...getInputValidationProps(productOptionEditValuesFormValidation.priceTakeaway, intl),
                    ...getInputSignedCurrencyProps(intl),
                })}
            />

            <p className={"col-span-2 -mt-3 text-sm text-gray-500 dark:text-slate-400"}>
                <FormattedMessage id={"optionPrice.tip"}/>
            </p>

            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={label("deposit", false)}
                leading={TextAddOn({text: "€"})}
                error={errors.deposit && errors.deposit.message}
                {...register("deposit", {
                    ...getInputValidationProps(productOptionEditValuesFormValidation.deposit, intl),
                    ...getInputCurrencyProps(intl),
                })}
            />

            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={label("plasticFee", false)}
                leading={TextAddOn({text: "€"})}
                error={errors.supFee && errors.supFee.message}
                {...register("supFee", {
                    ...getInputValidationProps(productOptionEditValuesFormValidation.supFee, intl),
                    ...getInputCurrencyProps(intl),
                })}
            />

            <Controller
                control={control}
                name={"vat"}
                render={({field: {value, onChange}}) => (
                    <Select<number>
                        label={label("vatRate", true)}
                        options={vatOptions}
                        value={vatOptions.find(option => option.value === value) ?? null}
                        displayValueEmpty={"-"}
                        onChange={(option: SelectOptionProps<number>) => onChange(option.value)}
                    />
                )}
            />

            <MultiSelectCombobox<Fulfillment>
                label={label("availability", false)}
                tip={!delivery && !takeaway ? intl.formatMessage({id: "optionNotOrderable"}) : undefined}
                options={fulfillmentOptions}
                value={[
                    ...(delivery ? ["delivery" as const] : []),
                    ...(takeaway ? ["takeaway" as const] : []),
                ]}
                onChange={(value: Fulfillment[]) => {
                    setValue("delivery", value.includes("delivery"));
                    setValue("takeaway", value.includes("takeaway"));
                }}
            />

            <div className={"col-span-2 flex flex-row items-center justify-end gap-3"}>
                <Button
                    color={"secondary"}
                    size={"sm"}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: e => {
                                e.preventDefault();
                                onCancel();
                            },
                        },
                    }}
                >
                    <FormattedMessage id={"cancel"}/>
                </Button>
                <Button
                    size={"sm"}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: e => {
                                e.preventDefault();
                                void submit();
                            },
                        },
                    }}
                >
                    <FormattedMessage id={"add"}/>
                </Button>
            </div>
        </form>
    );
};

export default NewOptionForm;